/*
 * Copyright (c) 2020. American Express Company
 */

import { LocalDate } from '@js-joda/core';
import * as _ from 'lodash';

import { CardModel } from 'src/types/card.model';
import { FullUserModel, UserUtils } from 'src/types/user.model';
import { REDUX_STORAGE_KEY } from 'src/bw/redux/store';
import LocalStorageService from './local-storage.service';
import LogoutService from './logout.service';
import SessionStorageService from './session-storage.service';
import { SearchParams } from 'src/types/searchParams.enum';

export const START_DATE_KEY = 'bw-st-dt';
export const TEST_USER_ID = 'bw-test-user';
export const IS_LOGGED_IN_KEY = 'bw-is-logged-in';
export const RETIRE_KEY = 'bw-retire';
export const MENTOR_KEY = 'bw-mentor';
export const SHOWN_ONBOARDING_FLOW = 'showed-bw-onboarding-flow';
export const SHOWED_RETIREMENT_FLOW = 'showed-bw-retirement-flow';
export const ZOOM = 'bw-zoom';
export const INTERNAL_ROUTE = 'bw-internal-route';
export const MONTHS_PER_PERIOD = 'bw-months-per-period';
export const USER_GROUP = 'user';
export const COBRAND = 'cobrand';
const SCROLL_RESTORATION = 'scroll-restoration';

export interface CacheEntry {
    value: string;
    expiration: Date;
}

export const SECONDS = 1;
export const MINUTES = 60 * SECONDS;
export const HOURS = 60 * MINUTES;
export const DEFAULT_TTL = HOURS * 24;

export enum Storage {
    session,
    local
}

export default class StorageService {
    private static groupedKey(key: string, group?: string): string {
        return group ? `${group}_${key}` : key;
    }

    public static async get(
        key: string,
        group?: string,
        callback?: Function,
        storage: Storage = Storage.session,
        TTL: number = DEFAULT_TTL
    ): Promise<JSON> {
        const value = await this.getString(key, group, storage, TTL, callback);
        let parsedJSON: JSON;

        try {
            parsedJSON = typeof value === 'string' ? JSON.parse(value) : value;
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            console.error(`${e}. failed to parse JSON for storage entry: ${value}`);
        }

        return parsedJSON;
    }

    public static async getString(
        key: string,
        group: string,
        storage: Storage = Storage.session,
        TTL?: number,
        callback?: Function
    ): Promise<any> {
        const value = this.getRaw(key, group, storage);
        if (!_.isEmpty(value)) return value;

        if (callback) {
            const result = await callback();
            this.set(key, group, result, storage, TTL);
            return result;
        }

        return undefined;
    }

    /**
     * Return the raw stored element, considering expiration, remove if it has expired
     * @param key
     * @param group
     * @param storage
     */
    public static getRaw(key: string, group: string, storage: Storage = Storage.session): any {
        const entry = this.getEntry(key, group, storage);
        if (!entry) return null;
        const now = new Date();

        // remove if there is an expiration and it has passed (falsy-expiration means never expire)
        if (entry.expiration && entry.expiration.valueOf() < now.valueOf()) {
            this.remove(key, group);
            return null;
        }

        return entry.value;
    }

    /**
     * Return the cache entry, containing the value, expiration and any meta data
     * @param key unique id for this element (in this groupUid)
     * @param group cache groupUid for this key
     * @param storage which major storage area to find this
     */
    public static getEntry(key: string, group: string, storage: Storage = Storage.session): CacheEntry {
        let cachedStr: string;
        switch (storage) {
            case Storage.session:
                cachedStr = SessionStorageService.get(this.groupedKey(key, group));
                break;
            case Storage.local:
                cachedStr = LocalStorageService.get(this.groupedKey(key, group));
                break;
        }
        if (!cachedStr) return null;
        const entry: CacheEntry = {} as CacheEntry;
        try {
            const storedJson = JSON.parse(cachedStr);
            entry.value = storedJson.value;
            entry.expiration = new Date(storedJson.expiration);
        } catch (e) {
            console.error('unable to parse cached item for key: ' + key);
        }
        return entry;
    }

    public static getReduxUserObject(noLogout: boolean = false): FullUserModel {
        const json = SessionStorageService.get(REDUX_STORAGE_KEY);
        if (!json) return undefined;

        let user: FullUserModel;
        try {
            user = JSON.parse(json).user.user;
        } catch (e) {
            console.error(e);
        }

        if (!user) {
            console.log('NO USER');
            if (!noLogout) {
                LogoutService.handleLogoutRedirect();
            }
            return undefined;
        }

        return UserUtils.deserializeDates(user);
    }

    public static removeReduxUser(): void {
        SessionStorageService.remove(REDUX_STORAGE_KEY);
    }

    public static set(
        key: string,
        group: string,
        value: any,
        storage: Storage = Storage.session,
        TTL?: number
    ): boolean {
        return this.setString(key, group, JSON.stringify(value), storage, TTL);
    }

    public static setString(
        key: string,
        group: string,
        item: string,
        storage: Storage = Storage.session,
        TTL?: number
    ): boolean {
        return this.setRaw(key, group, item, storage, TTL);
    }

    public static setRaw(
        key: string,
        group: string,
        item: string,
        storage: Storage = Storage.session,
        TTL: number = DEFAULT_TTL
    ): boolean {
        const expire = TTL && new Date(new Date().valueOf() + TTL * 1000);
        const entry: CacheEntry = {
            value: item,
            expiration: expire
        };

        return this.setEntry(key, group, entry, storage);
    }

    public static setEntry(key: string, group: string, entry: CacheEntry, storage: Storage = Storage.session): boolean {
        switch (storage) {
            case Storage.session:
                return SessionStorageService.set(this.groupedKey(key, group), JSON.stringify(entry));
            case Storage.local:
                return LocalStorageService.set(this.groupedKey(key, group), JSON.stringify(entry));
        }
    }

    public static remove(key: string, group: string, storage: Storage = Storage.session): void {
        const realKey: string = this.groupedKey(key, group);
        switch (storage) {
            case Storage.session:
                SessionStorageService.remove(realKey);
                break;
            case Storage.local:
                LocalStorageService.remove(realKey);
        }
    }

    public static setTestUserId(userId: string): boolean {
        return this.setRaw(TEST_USER_ID, null, userId, Storage.session, DEFAULT_TTL);
    }

    public static getTestUserId(): string {
        return this.getRaw(TEST_USER_ID, null, Storage.session);
    }

    /**
     *  app-specific entries for the storage service
     */
    public static getIsLoggedIn(): boolean {
        return this.getRaw(IS_LOGGED_IN_KEY, null, Storage.local) === 'true';
    }

    public static setIsLoggedIn(status: boolean): boolean {
        return this.setRaw(IS_LOGGED_IN_KEY, null, status.toString(), Storage.local, DEFAULT_TTL);
    }

    public static getCard(cardId: number, user?: FullUserModel): any {
        user = user || this.getReduxUserObject();
        if (!user) return {};
        const selection: any = {};
        _.forEach(user.cards, (card: CardModel, cardIndex: number) => {
            CardModel.deserializeDates(card);
            if (Number(card.id) === Number(cardId)) {
                selection.card = card;
                selection.cardIndex = cardIndex;
            }
        });

        return selection;
    }

    /** helper function to get CardModel directly from getCard above, since it's common */
    public static getCardModel(cardId: number): CardModel {
        return _.get(this.getCard(cardId), 'card');
    }

    /** return all cards with the given description string, eg: 'buy new home' */
    public static getCards(description: String): CardModel[] {
        const user: FullUserModel = this.getReduxUserObject();
        const cards: CardModel[] = user.cards.map((card) => CardModel.deserializeDates(card));
        return _.filter(cards, (card: CardModel) => card.description === description);
    }

    public static addCard(card: CardModel): void {
        const user: FullUserModel = this.getReduxUserObject();
        if (!user) return;
        user.cards.push(card);
    }

    public static deleteCard(cardId: number): void {
        const user: FullUserModel = this.getReduxUserObject();
        if (!user) return;
        const cardData: { card: CardModel; cardIndex: number } = this.getCard(cardId);
        user.cards.splice(cardData.cardIndex, 1);
    }

    public static updateCard(card: CardModel, user?: FullUserModel): void {
        if (!user) {
            user = this.getReduxUserObject();
        }
        if (!user) return;
        const cardData: { card: CardModel; cardIndex: number } = this.getCard(card.id || 0, user);

        if (cardData?.cardIndex) {
            user.cards[cardData.cardIndex].startDate = card.startDate;
            user.cards[cardData.cardIndex].endDate = card.endDate;
            user.cards[cardData.cardIndex].attributes = card.attributes;
            user.cards[cardData.cardIndex].startIncomes = card.startIncomes;
            user.cards[cardData.cardIndex].modifyIncomes = card.modifyIncomes;
            user.cards[cardData.cardIndex].yPosition = card.yPosition;
        } else {
            user.cards = [...user.cards, card];
        }
    }

    public static getStartDate(): LocalDate {
        const dateStr: string = this.getRaw(START_DATE_KEY, null, Storage.session);
        return dateStr && LocalDate.parse(dateStr);
    }

    public static setStartDate(startDate: string): void {
        this.setRaw(START_DATE_KEY, null, startDate, Storage.session);
    }

    public static removeStartDate(): void {
        this.remove(START_DATE_KEY, null, Storage.session);
    }

    public static setInternalRoute(internal: boolean): void {
        this.setRaw(INTERNAL_ROUTE, null, internal.toString());
    }

    public static getInternalRoute(): boolean {
        return Boolean(this.getRaw(INTERNAL_ROUTE, null));
    }

    public static setZoom(zoomValue: string): void {
        this.setRaw(ZOOM, null, zoomValue);
    }

    public static getZoom(): string {
        return this.getRaw(ZOOM, null);
    }

    public static setRetire(retireValue: number): void {
        this.setRaw(RETIRE_KEY, null, retireValue.toString(), Storage.session);
    }

    public static getRetire(): number {
        return Number(this.getRaw(RETIRE_KEY, null, Storage.session));
    }

    public static getSavedMentor(): number {
        return this.getRaw(MENTOR_KEY, USER_GROUP, Storage.session);
    }

    public static setSavedMentor(mentor: any): void {
        this.setRaw(MENTOR_KEY, USER_GROUP, mentor, Storage.session);
    }

    public static setMonthsPerPeriod(mppValue: number | string): void {
        this.setRaw(MONTHS_PER_PERIOD, null, String(mppValue));
    }

    public static getMonthsPerPeriod(): number {
        return Number(this.getRaw(MONTHS_PER_PERIOD, null));
    }

    public static getShowedRetirementFlow(): boolean {
        return Boolean(this.getRaw(SHOWED_RETIREMENT_FLOW, null, Storage.session));
    }

    public static setShowedRetirementFlow(showed: boolean): void {
        this.set(SHOWED_RETIREMENT_FLOW, null, showed.toString(), Storage.session);
    }

    public static setCobrand(cobrand: string): void {
        this.setRaw(COBRAND, null, cobrand, Storage.session);
    }

    public static getCobrand(): string {
        return this.getRaw(COBRAND, null, Storage.session);
    }

    public static getShownOnboarding(): boolean {
        return Boolean(this.getRaw(SHOWN_ONBOARDING_FLOW, null, Storage.session));
    }

    public static getScrollRestorationForPage(page: string): number {
        return +(this.getRaw(`${SCROLL_RESTORATION}-${page}`, null, Storage.session) || '0');
    }

    public static setScrollRestorationForPage(page: string, scrollPosition: number = 0): void {
        this.setRaw(`${SCROLL_RESTORATION}-${page}`, null, scrollPosition.toString(), Storage.session);
    }

    public static getNative(): boolean {
        return Boolean(this.getRaw(SearchParams.NATIVE, null, Storage.session));
    }

    public static setNative(): void {
        this.setRaw(SearchParams.NATIVE, null, 'true', Storage.session);
    }

    public static removeNative(): void {
        this.remove(SearchParams.NATIVE, null, Storage.session);
    }
}
